import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getFaturasCotacao } from "@/services/payment";
import type { CotacaoEcommerce } from "@/types/Cotacao";
import type { FaturaCotacao } from "@/types/Payment";
import { getInvoiceStatusCategory, isInvoiceUnpaid } from "@/types/Payment";
import { openInNewTab, sanitizeUrl } from "@/utils/url-helpers";
import { ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";

interface InvoicesCardProps {
  cotacao: CotacaoEcommerce;
  onHasUnpaidInvoices?: (hasUnpaid: boolean) => void;
}

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "—";
  const num = parseFloat(value);
  if (isNaN(num)) return "—";
  return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("pt-BR");
}

const STATUS_BADGE: Record<
  "paid" | "unpaid" | "overdue" | "reversed" | "unknown",
  { label: string; className: string }
> = {
  paid: {
    label: "Pago",
    className: "bg-green-100 text-green-700 border-green-200",
  },
  unpaid: {
    label: "A vencer",
    className: "bg-yellow-100 text-yellow-700 border-yellow-200",
  },
  overdue: {
    label: "Vencido",
    className: "bg-red-100 text-red-700 border-red-200",
  },
  reversed: {
    label: "Estornado",
    className: "bg-gray-100 text-gray-600 border-gray-200",
  },
  unknown: {
    label: "—",
    className: "bg-gray-100 text-gray-500 border-gray-200",
  },
};

function isPixOrBoleto(porDescricao: string): boolean {
  const lower = porDescricao.toLowerCase();
  return lower.includes("pix") || lower.includes("boleto");
}

function resolvePaymentLink(
  fatura: FaturaCotacao,
  cotacao: CotacaoEcommerce,
): string | null {
  if (!isInvoiceUnpaid(fatura.tts_descricao)) return null;

  if (
    fatura.cre_origem_emissao === "Site" &&
    isPixOrBoleto(fatura.por_descricao)
  ) {
    return `${import.meta.env.VITE_APP_URL}/cotacao/${cotacao.cve_codigo}?token=${encodeURIComponent(cotacao.cve_token)}&checkout=true`;
  }

  if (fatura.cre_url_link) {
    return sanitizeUrl(fatura.cre_url_link);
  }

  return null;
}

function InvoiceRow({
  fatura,
  cotacao,
}: {
  fatura: FaturaCotacao;
  cotacao: CotacaoEcommerce;
}) {
  const category = getInvoiceStatusCategory(fatura.tts_descricao);
  const badge = STATUS_BADGE[category];
  const paymentLink = resolvePaymentLink(fatura, cotacao);
  const receiptLink =
    category === "paid" && fatura.cre_url_link
      ? sanitizeUrl(fatura.cre_url_link)
      : null;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg bg-gray-50 border border-gray-200">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 flex-1">
        <Badge
          className={`self-start sm:self-auto text-xs font-semibold rounded-full border px-2 py-0.5 ${badge.className}`}
        >
          {badge.label}
        </Badge>

        <div className="grid grid-cols-2 sm:flex sm:flex-row sm:gap-6 gap-x-4 gap-y-1 text-sm">
          <div>
            <p className="text-xs text-gray-500">Vencimento</p>
            <p className="font-medium text-gray-800">
              {formatDate(fatura.cre_data_vencimento)}
            </p>
          </div>

          <div>
            <p className="text-xs text-gray-500">Forma de pagamento</p>
            <p className="font-medium text-gray-800">
              {fatura.por_descricao || "—"}
            </p>
          </div>

          <div>
            <p className="text-xs text-gray-500">Valor</p>
            <p className="font-medium text-gray-800">
              {formatCurrency(fatura.cre_valor)}
            </p>
          </div>

          {fatura.cre_data_pag && (
            <div>
              <p className="text-xs text-gray-500">Pago em</p>
              <p className="font-medium text-gray-800">
                {formatDate(fatura.cre_data_pag)}
              </p>
            </div>
          )}
        </div>
      </div>

      {paymentLink && (
        <Button
          variant="default"
          size="sm"
          className="self-start sm:self-auto shrink-0"
          onClick={() => openInNewTab(paymentLink)}
        >
          Ir para pagamento
          <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      )}

      {receiptLink && (
        <Button
          variant="default"
          size="sm"
          className="self-start sm:self-auto shrink-0"
          onClick={() => openInNewTab(receiptLink)}
        >
          Visualizar Comprovante
          <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      )}
    </div>
  );
}

function InvoicesSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {[0, 1].map((i) => (
        <div
          key={i}
          className="h-[72px] rounded-lg bg-gray-100 animate-pulse border border-gray-200"
        />
      ))}
    </div>
  );
}

export function InvoicesCard({
  cotacao,
  onHasUnpaidInvoices,
}: InvoicesCardProps) {
  const [faturas, setFaturas] = useState<FaturaCotacao[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchFaturas = async () => {
      try {
        const data = await getFaturasCotacao(cotacao.cve_codigo);
        if (!cancelled) {
          setFaturas(data);
          if (onHasUnpaidInvoices) {
            const hasUnpaid = data.some((f) =>
              isInvoiceUnpaid(f.tts_descricao),
            );
            onHasUnpaidInvoices(hasUnpaid);
          }
        }
      } catch {
        // erro silencioso — loading é encerrado no finally
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchFaturas();
    return () => {
      cancelled = true;
    };
  }, [cotacao.cve_codigo, onHasUnpaidInvoices]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-n-green">Faturas:</h3>
        <InvoicesSkeleton />
      </div>
    );
  }

  if (faturas.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-n-green">Faturas:</h3>
      <div className="flex flex-col gap-3">
        {faturas.map((fatura) => (
          <InvoiceRow
            key={fatura.cre_codigo}
            fatura={fatura}
            cotacao={cotacao}
          />
        ))}
      </div>
    </div>
  );
}
